import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarRange, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Segmented } from "@/components/ui/segmented";
import {
  INSIGHTS_PERIODS,
  type CompareMode,
  type InsightsPeriod,
  type InsightsRangeQuery,
} from "@/api/workflow-insights";

/**
 * The window Insights is showing, and the baseline it is measured against.
 * A preset is the common case; `since`/`until` (YYYY-MM-DD) take over when
 * someone picks their own dates, which is what makes "the two days since we
 * changed the model" comparable with "the two days before".
 */
export interface InsightsRangeValue {
  period: InsightsPeriod | null;
  since: string | null;
  until: string | null;
  compare: CompareMode;
  compareSince: string | null;
  compareUntil: string | null;
}

export const DEFAULT_RANGE: InsightsRangeValue = {
  period: "30d",
  since: null,
  until: null,
  compare: "previous",
  compareSince: null,
  compareUntil: null,
};

const isPeriod = (v: string | null): v is InsightsPeriod =>
  !!v && (INSIGHTS_PERIODS as readonly string[]).includes(v);
const isDate = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Only send what the server needs; it fills in every default itself. */
export function rangeQuery(v: InsightsRangeValue): InsightsRangeQuery {
  const custom = isDate(v.since);
  return {
    period: custom ? undefined : (v.period ?? "30d"),
    since: custom ? v.since! : undefined,
    until: custom && isDate(v.until) ? v.until! : undefined,
    compare: v.compare === "previous" ? undefined : v.compare,
    compareSince: v.compare === "custom" && isDate(v.compareSince) ? v.compareSince! : undefined,
    compareUntil: v.compare === "custom" && isDate(v.compareUntil) ? v.compareUntil! : undefined,
  };
}

/** A stable key for react-query, so switching windows refetches. */
export function rangeKey(v: InsightsRangeValue): string {
  const q = rangeQuery(v);
  return [q.period ?? "", q.since ?? "", q.until ?? "", q.compare ?? "", q.compareSince ?? "", q.compareUntil ?? ""].join("|");
}

/** Carry the window through links (drill-in, back) exactly as selected. */
export function rangeSearch(v: InsightsRangeValue): string {
  const q = rangeQuery(v);
  const params = new URLSearchParams();
  for (const [k, val] of Object.entries(q)) if (val) params.set(k, String(val));
  return params.toString();
}

/**
 * Read the window from the URL so a link carries it, exactly as `?period=`
 * did before. Anything unparseable falls back to the 30d default rather than
 * rendering an empty screen.
 */
export function useInsightsRange(): [InsightsRangeValue, (next: InsightsRangeValue) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const since = searchParams.get("since");
  const periodParam = searchParams.get("period");
  const compareParam = searchParams.get("compare");
  const value: InsightsRangeValue = {
    period: isDate(since) ? null : isPeriod(periodParam) ? periodParam : "30d",
    since: isDate(since) ? since : null,
    until: isDate(since) && isDate(searchParams.get("until")) ? searchParams.get("until") : null,
    compare: compareParam === "none" || compareParam === "custom" ? compareParam : "previous",
    compareSince: isDate(searchParams.get("compareSince")) ? searchParams.get("compareSince") : null,
    compareUntil: isDate(searchParams.get("compareUntil")) ? searchParams.get("compareUntil") : null,
  };
  const set = (next: InsightsRangeValue) => {
    const params = new URLSearchParams(searchParams);
    const q = rangeQuery(next);
    for (const key of ["period", "since", "until", "compare", "compareSince", "compareUntil"] as const) {
      const v = q[key];
      if (v) params.set(key, String(v));
      else params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };
  return [value, set];
}

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

/**
 * Preset buttons, a custom window, and the baseline to compare against.
 * The two popovers are plain absolutely-positioned panels — the app has no
 * popover primitive, and a <details> would not close on outside clicks.
 */
export function InsightsRangePicker({
  value,
  onChange,
  className,
}: {
  value: InsightsRangeValue;
  onChange: (next: InsightsRangeValue) => void;
  className?: string;
}) {
  const [open, setOpen] = useState<null | "range" | "compare">(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const custom = !value.period;
  const customLabel = custom
    ? `${value.since}${value.until && value.until !== value.since ? ` → ${value.until}` : ""}`
    : "Custom";

  const compareLabel =
    value.compare === "none"
      ? "No comparison"
      : value.compare === "custom" && value.compareSince
        ? `vs ${value.compareSince}${value.compareUntil && value.compareUntil !== value.compareSince ? ` → ${value.compareUntil}` : ""}`
        : "vs previous period";

  return (
    <div ref={wrapRef} className={cn("relative flex flex-wrap items-center gap-2", className)}>
      <Segmented
        aria-label="Period"
        value={value.period ?? ("custom" as InsightsPeriod)}
        onChange={(p) => onChange({ ...value, period: p, since: null, until: null })}
        options={INSIGHTS_PERIODS.map((p) => ({ value: p, label: p }))}
      />

      <button
        type="button"
        onClick={() => setOpen(open === "range" ? null : "range")}
        aria-expanded={open === "range"}
        className={cn(
          "inline-flex h-[26px] max-w-[200px] items-center gap-1.5 truncate rounded-md border px-[9px] text-[12px] font-medium transition-colors hover:bg-surface-hover",
          custom ? "border-text-muted text-text-primary" : "border-border text-text-secondary hover:text-text-primary",
        )}
      >
        <CalendarRange size={12} />
        <span className="truncate">{customLabel}</span>
      </button>

      <button
        type="button"
        onClick={() => setOpen(open === "compare" ? null : "compare")}
        aria-expanded={open === "compare"}
        className="inline-flex h-[26px] max-w-[220px] items-center gap-1.5 truncate rounded-md border border-border px-[9px] text-[12px] font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
      >
        <span className="truncate">{compareLabel}</span>
        <ChevronDown size={12} />
      </button>

      {open === "range" && (
        <Panel title="Custom range">
          <DateField
            label="From"
            value={value.since ?? daysAgo(2)}
            max={value.until ?? today()}
            onChange={(since) => onChange({ ...value, period: null, since, until: value.until ?? today() })}
          />
          <DateField
            label="To"
            value={value.until ?? today()}
            min={value.since ?? undefined}
            max={today()}
            onChange={(until) => onChange({ ...value, period: null, since: value.since ?? daysAgo(2), until })}
          />
          <p className="text-[11px] text-text-muted">Both days are included. Times are UTC.</p>
          {custom && (
            <button type="button" className={LINK} onClick={() => onChange({ ...value, period: "30d", since: null, until: null })}>
              Back to presets
            </button>
          )}
        </Panel>
      )}

      {open === "compare" && (
        <Panel title="Compare with">
          <Segmented
            aria-label="Comparison"
            className="w-full"
            value={value.compare}
            onChange={(compare) => onChange({ ...value, compare })}
            options={[
              { value: "previous" as CompareMode, label: "Previous" },
              { value: "custom" as CompareMode, label: "Custom" },
              { value: "none" as CompareMode, label: "None" },
            ]}
          />
          {value.compare === "custom" && (
            <>
              <DateField
                label="From"
                value={value.compareSince ?? daysAgo(9)}
                max={value.compareUntil ?? today()}
                onChange={(compareSince) =>
                  onChange({ ...value, compareSince, compareUntil: value.compareUntil ?? daysAgo(7) })
                }
              />
              <DateField
                label="To"
                value={value.compareUntil ?? daysAgo(7)}
                min={value.compareSince ?? undefined}
                max={today()}
                onChange={(compareUntil) =>
                  onChange({ ...value, compareSince: value.compareSince ?? daysAgo(9), compareUntil })
                }
              />
            </>
          )}
          <p className="text-[11px] text-text-muted">
            {value.compare === "none"
              ? "KPIs show this window only."
              : value.compare === "custom"
                ? "Every KPI is measured against this baseline window."
                : "The equal-length window immediately before the one selected."}
          </p>
        </Panel>
      )}
    </div>
  );
}

const LINK = "self-start text-[11.5px] font-medium text-text-secondary underline-offset-2 hover:text-text-primary hover:underline";

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="absolute right-0 top-[32px] z-30 flex w-[240px] flex-col gap-2.5 rounded-md border border-border bg-surface p-3 shadow-lg">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">{title}</span>
      {children}
    </div>
  );
}

function DateField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-[12px] text-text-secondary">
      {label}
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className="h-[26px] rounded-md border border-border bg-transparent px-[7px] text-[12px] text-text-primary outline-none focus:border-text-muted"
      />
    </label>
  );
}
